import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { applicationsApi } from '@/features/applications/api/applicationsApi';
import { ApplicationReviewHeader } from '@/features/applications/components/ApplicationReviewHeader';
import { ApplicationReviewStepper } from '@/features/applications/components/ApplicationReviewStepper';
import { StepContent } from '@/features/applications/components/StepContent';
import { StepNavigation } from '@/features/applications/components/StepNavigation';
import { REVIEW_STEPS } from '@/features/applications/constants/review-steps.constants';
import { isApplicationApproved } from '@/features/applications/utils/application-status.utils';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

export function ApplicationReviewPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // Data fetching avec useQuery
  const {
    data: application,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['application', id],
    queryFn: async () => {
      const data = await applicationsApi.getById(id!);
      return data;
    },
    enabled: !!id,
  });

  // States - DOIVENT être appelés inconditionnellement
  const [activeStep, setActiveStep] = useState(0);
  const [visitedSteps, setVisitedSteps] = useState<Set<number>>(new Set([0]));

  // Callbacks - DOIVENT être appelés inconditionnellement
  const handleBack = useCallback(() => {
    navigate('/applications');
  }, [navigate]);

  const handleStepClick = useCallback(
    (step: number) => {
      if (!application || step < 0 || step >= REVIEW_STEPS.length) {
        return;
      }

      // Navigation libre : toujours autoriser le clic sur n'importe quelle étape
      setActiveStep(step);
      setVisitedSteps((prev) => new Set(prev).add(step));
    },
    [application],
  );

  const handleNext = useCallback(() => {
    const nextStep = activeStep + 1;
    if (nextStep < REVIEW_STEPS.length) {
      handleStepClick(nextStep);
    }
  }, [activeStep, handleStepClick]);

  const handlePrevious = useCallback(() => {
    const previousStep = activeStep - 1;
    if (previousStep >= 0) {
      handleStepClick(previousStep);
    }
  }, [activeStep, handleStepClick]);

  // Early returns APRÈS tous les hooks
  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <LoadingSpinner message="Chargement du dossier..." submessage="Veuillez patienter" size="lg" />
      </div>
    );
  }

  if (error || !application) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-slate-900 mb-2">Dossier introuvable</h2>
          <p className="text-slate-600 mb-6">
            {error instanceof Error ? error.message : "Ce dossier de candidature n'existe pas ou a été supprimé."}
          </p>
          <button
            onClick={handleBack}
            className="px-6 py-2.5 bg-[#00758d] text-white rounded-lg font-medium hover:bg-[#006070] transition-colors"
          >
            Retour aux candidatures
          </button>
        </div>
      </div>
    );
  }

  const allStepsVisited = visitedSteps.size === REVIEW_STEPS.length;
  const isFreeNavigation = allStepsVisited || isApplicationApproved(application.status);

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <ApplicationReviewHeader application={application} onBack={handleBack} />

        {/* Texte explicatif du récapitulatif */}
        <div
          className="border rounded-xl p-6 mb-6 shadow-sm"
          style={{
            background: 'linear-gradient(to right, #00758d15, #f8fafc)',
            borderColor: '#00758d30',
          }}
        >
          <div className="flex items-start gap-4">
            <div
              className="flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: '#00758d20' }}
            >
              <svg
                className="w-6 h-6"
                style={{ color: '#00758d' }}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-bold text-slate-800 mb-2">Récapitulatif du dossier de candidature</h2>
              <p className="text-sm text-slate-600 leading-relaxed mb-3">
                Cette page présente l'ensemble des informations soumises par le client pour sa demande d'espace.
                Parcourez chaque section pour examiner les détails du client, les caractéristiques de la réservation,
                les options sélectionnées, les informations financières et les documents fournis.
              </p>
              <div className="flex items-center gap-2 text-sm">
                <span
                  className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg font-medium"
                  style={{
                    backgroundColor: '#00758d20',
                    color: '#00758d',
                  }}
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                      clipRule="evenodd"
                    />
                  </svg>
                  Consultez toutes les sections avant de valider
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Stepper Card */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <ApplicationReviewStepper
            activeStep={activeStep}
            visitedSteps={visitedSteps}
            onStepClick={handleStepClick}
            allStepsVisited={isFreeNavigation}
          />
        </div>

        {/* Content */}
        <StepContent step={activeStep} application={application} allStepsVisited={isFreeNavigation} />

        {/* Navigation */}
        <StepNavigation
          activeStep={activeStep}
          totalSteps={REVIEW_STEPS.length}
          onNext={handleNext}
          onPrevious={handlePrevious}
        />
      </div>
    </div>
  );
}
